import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  MapPinIcon,
  TruckIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { usePaginatedTable } from '../../hooks/usePaginatedTable';
import Pagination from '../Common/Pagination';
import DeliveryReviewModal from './DeliveryReviewModal';

interface Order {
  id: number;
  uuid: string;
  customer_name: string;
  customer_email?: string;
  dispatcher_name?: string;
  dispatcher_id?: number;
  pickup_location: string;
  dropoff_location: string;
  status: string;
  final_price: number;
  created_at: string;
  completed_at?: string;
  vehicle_type: string;
  package_description?: string;
  package_type?: string;
  payment_method?: string;
  payment_status?: string;
  total_distance_km?: number;
  total_duration_minutes?: number;
  admin_approval_status?: string;
  admin_notes?: string;
  reviewed_at?: string;
  pickup_photo_url?: string;
  delivery_photo_url?: string;
  special_instructions?: string;
  delivery_notes?: string;
  stops: any[];
}

const OrderManagementCRUD: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [deletingOrder, setDeletingOrder] = useState<Order | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [editForm, setEditForm] = useState({
    status: '',
    payment_status: '',
    delivery_notes: '',
    special_instructions: ''
  });
  const { formatPrice } = useCurrency();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await adminAPI.getAllOrders();
      setOrders(response.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toString().includes(searchTerm) ||
                         order.uuid.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.dropoff_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.pickup_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.status.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter === 'pending') {
      matchesStatus = order.status === 'pending';
    } else if (statusFilter === 'active') {
      matchesStatus = ['assigned', 'picked_up', 'in_transit'].includes(order.status);
    } else if (statusFilter === 'completed') {
      matchesStatus = order.status === 'delivered';
    } else if (statusFilter === 'cancelled') {
      matchesStatus = order.status === 'cancelled';
    }
    
    const matchesApproval = approvalFilter === 'all' || 
                           (approvalFilter === 'pending_review' && order.admin_approval_status === 'pending_review') ||
                           (approvalFilter === 'approved' && order.admin_approval_status === 'approved') ||
                           (approvalFilter === 'needs_revision' && order.admin_approval_status === 'needs_revision');
    return matchesSearch && matchesStatus && matchesApproval;
  });

  const {
    paginatedItems,
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    goToPage,
    setItemsPerPage,
    resetPagination
  } = usePaginatedTable({
    items: filteredOrders,
    initialItemsPerPage: 10
  });

  useEffect(() => {
    resetPagination();
  }, [searchTerm, statusFilter, approvalFilter]);

  const handleEditClick = (order: Order) => {
    setEditingOrder(order);
    setEditForm({
      status: order.status || '',
      payment_status: order.payment_status || '',
      delivery_notes: order.delivery_notes || '',
      special_instructions: order.special_instructions || ''
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrder) return;

    if (!editForm.status) {
      setNotification({ type: 'error', message: 'Status is required' });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    try {
      await adminAPI.updateOrder(editingOrder.id, editForm);
      await fetchOrders();
      setEditingOrder(null);
      setNotification({ type: 'success', message: 'Order updated successfully!' });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error updating order:', error);
      setNotification({ type: 'error', message: error.response?.data?.detail || 'Failed to update order' });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleDeleteClick = (order: Order) => {
    setDeletingOrder(order);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingOrder) return;

    try {
      await adminAPI.deleteOrder(deletingOrder.id);
      await fetchOrders();
      setDeletingOrder(null);
      setNotification({ type: 'success', message: 'Order deleted successfully!' });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error deleting order:', error);
      setNotification({ type: 'error', message: error.response?.data?.detail || 'Failed to delete order' });
      setTimeout(() => setNotification(null), 5000);
      setDeletingOrder(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      picked_up: 'bg-volexx-blue-100 text-volexx-blue-800',
      in_transit: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      pending: 'Pending',
      assigned: 'Assigned',
      picked_up: 'Picked Up',
      in_transit: 'In Transit',
      delivered: 'Delivered',
      cancelled: 'Cancelled'
    };
    return texts[status] || status;
  };

  const getApprovalBadge = (approvalStatus?: string) => {
    if (!approvalStatus) return null;
    const badges: Record<string, string> = {
      pending_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      needs_revision: 'bg-red-100 text-red-800'
    };
    return badges[approvalStatus] || 'bg-gray-100 text-gray-800';
  };

  const getApprovalText = (approvalStatus?: string) => {
    if (!approvalStatus) return '-';
    const texts: Record<string, string> = {
      pending_review: 'Pending Review',
      approved: 'Approved',
      needs_revision: 'Needs Revision'
    };
    return texts[approvalStatus] || approvalStatus;
  };

  const handleReviewOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowReviewModal(true);
  };

  const handleReviewModalClose = () => {
    setShowReviewModal(false);
    setSelectedOrder(null);
  };

  const handleReviewUpdate = () => {
    fetchOrders();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-volexx-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`rounded-volexx-lg shadow-volexx border ${notification.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} px-6 py-4 max-w-md`}>
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
              ) : (
                <XCircleIcon className="h-6 w-6 text-red-600 mr-3" />
              )}
              <p className={`font-semibold font-poppins ${notification.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">Order Management</h1>
          <p className="mt-2 text-gray-600 font-poppins">Full control over all platform orders</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order ID, customer, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
            >
              <option value="all">All Orders</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={approvalFilter}
              onChange={(e) => setApprovalFilter(e.target.value)}
              className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
            >
              <option value="all">All Approval Status</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="needs_revision">Needs Revision</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Total Orders</p>
            <p className="text-2xl font-bold text-gray-900 font-poppins">{orders.length}</p>
          </div>
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Filtered</p>
            <p className="text-2xl font-bold text-volexx-blue-600 font-poppins">{filteredOrders.length}</p>
          </div>
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Pending</p>
            <p className="text-2xl font-bold text-yellow-600 font-poppins">{orders.filter(o => o.status === 'pending').length}</p>
          </div>
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Delivered</p>
            <p className="text-2xl font-bold text-green-600 font-poppins">{orders.filter(o => o.status === 'delivered').length}</p>
          </div>
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4">
            <p className="text-sm text-gray-600 font-poppins">Total Revenue</p>
            <p className="text-2xl font-bold text-green-600 font-poppins">{formatPrice(orders.reduce((sum, o) => sum + (o.final_price || 0), 0))}</p>
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Dispatcher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Route
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedItems.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900 font-poppins">#{order.id}</div>
                      <div className="text-xs text-gray-500 font-poppins">{new Date(order.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 font-poppins">{order.customer_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-poppins">
                        {order.dispatcher_name || <span className="text-gray-400">Unassigned</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-start text-sm max-w-xs">
                        <MapPinIcon className="h-4 w-4 text-green-500 mr-1 mt-0.5 flex-shrink-0" />
                        <div className="font-poppins truncate">
                          <div className="text-gray-900 truncate">{order.pickup_location}</div>
                          <div className="text-gray-500 mt-1 truncate">→ {order.dropoff_location}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-volexx font-poppins ${getStatusBadge(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900 font-poppins">{formatPrice(order.final_price)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                      <button 
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="text-volexx-blue-600 hover:text-volexx-blue-900 font-semibold font-poppins transition-colors"
                      >
                        View
                      </button>
                      <button 
                        onClick={() => handleEditClick(order)}
                        className="text-green-600 hover:text-green-900 font-semibold font-poppins transition-colors inline-flex items-center"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(order)}
                        className="text-red-600 hover:text-red-900 font-semibold font-poppins transition-colors inline-flex items-center"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <TruckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-poppins">No orders found matching your criteria</p>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      {/* Edit Order Modal */}
      {editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-volexx-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 font-poppins">Edit Order #{editingOrder.id}</h2>
              <button
                onClick={() => setEditingOrder(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                    Order Status *
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    required
                  >
                    <option value="">Select status</option>
                    <option value="pending">Pending</option>
                    <option value="assigned">Assigned</option>
                    <option value="picked_up">Picked Up</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                    Payment Status
                  </label>
                  <select
                    value={editForm.payment_status}
                    onChange={(e) => setEditForm({ ...editForm, payment_status: e.target.value })}
                    className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                  >
                    <option value="">Select payment status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                  Special Instructions
                </label>
                <textarea
                  value={editForm.special_instructions}
                  onChange={(e) => setEditForm({ ...editForm, special_instructions: e.target.value })}
                  rows={3}
                  className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                  placeholder="Add special instructions for this order"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                  Delivery Notes
                </label>
                <textarea
                  value={editForm.delivery_notes}
                  onChange={(e) => setEditForm({ ...editForm, delivery_notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                  placeholder="Add delivery notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setEditingOrder(null)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-volexx font-semibold font-poppins hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-volexx-blue-600 text-white rounded-volexx font-semibold font-poppins hover:bg-volexx-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-volexx-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 font-poppins text-center mb-2">Delete Order</h2>
              <p className="text-gray-600 font-poppins text-center mb-6">
                Are you sure you want to delete Order <span className="font-bold">#{deletingOrder.id}</span>? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDeletingOrder(null)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-volexx font-semibold font-poppins hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-6 py-2 bg-red-600 text-white rounded-volexx font-semibold font-poppins hover:bg-red-700 transition-colors"
                >
                  Delete Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Review Modal */}
      {showReviewModal && selectedOrder && (
        <DeliveryReviewModal
          order={selectedOrder}
          onClose={handleReviewModalClose}
          onUpdate={handleReviewUpdate}
        />
      )}
    </div>
  );
};

export default OrderManagementCRUD;
